import { Box, Button, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Contract } from '@/api/generated/models';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { WeekReportStatus } from '@/api/generated/models/weekReportStatus';
import { useListObjects } from '@/api/generated/objects/objects';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import { useSearchWeekReports } from '@/api/generated/week-reports/week-reports';
import {
  CardSection,
  ListPagination,
  ListTotalCard,
  ListTotalRow,
  MobileFab,
  MobileListFilters,
  PageHeading,
  RowActionsMenu,
  SearchField,
  SelectField,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_DATE_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  TABLE_WEEK_PRIMARY_COL_WIDTH,
  tableCenteredCellStyle,
  tableMinWidth,
  tableRowStyle
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';
import { GenerateWeekReportModal } from '@/features/week-reports/components/GenerateWeekReportModal';
import { formatWeekLabel, statusConfig } from '@/features/week-reports/weekReportDisplay';
import { formatAmount, formatDate, otherPartyName } from '@/utils/format';
import { usePageSize } from '@/utils/usePageSize';
import { useUrlNullableParam, useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

const cellStyle = { color: 'var(--color-text-secondary)' };

const DESKTOP_COLUMNS: [string, number, ('center' | 'right' | undefined)?][] = [
  ['weekReports.colPeriod', TABLE_WEEK_PRIMARY_COL_WIDTH],
  ['weekReports.colContractObjectPartner', 260],
  ['weekReports.colAmount', 120, 'right'],
  ['weekReports.colSentDate', TABLE_DATE_COL_WIDTH],
  ['weekReports.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
];

const TABLE_MIN_WIDTH = tableMinWidth(
  [...DESKTOP_COLUMNS.map(([, width]) => width), TABLE_ACTION_COL_WIDTH],
  20
);

const HeaderIntro = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  return (
    <PageHeading
      title={t('weekReports.title')}
      subtitle={
        <>
          {t('weekReports.subtitle')}{' '}
          <Text span inherit fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {user?.orgName}
          </Text>
        </>
      }
    />
  );
};

export const WeekReportsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();
  const [generateOpened, setGenerateOpened] = useState(false);
  const [search, setSearch] = useUrlTextParam('search');
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [objectFilter, setObjectFilter] = useUrlNullableParam('objectId');
  const [counterpartyFilter, setCounterpartyFilter] = useUrlNullableParam('counterpartyOrgId');

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, objectFilter, counterpartyFilter, pageSize]);

  // refetchOnMount: 'always' — lista mora biti svježa svaki put kad se uđe na
  // ekran, ne samo kad je staleTime istekao (npr. netom predan tjedni izvještaj
  // mora odmah biti vidljiv, ne tek za 5 min).
  const { data, isLoading } = useSearchWeekReports(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      objectId: objectFilter ?? undefined,
      counterpartyOrgId: counterpartyFilter ?? undefined
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageItems = data?.content ?? [];
  // Traka UKUPNO = zbroj TRENUTNE stranice (ne cijelog filtriranog skupa).
  const totalAmount = pageItems.reduce((sum, report) => sum + (report.totalAmount ?? 0), 0);
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const statusOptions = [
    { value: 'ALL', label: t('common.filters.allStatuses') },
    ...Object.values(WeekReportStatus).map((status) => ({
      value: status,
      label: t(statusConfig[status].labelKey)
    }))
  ];

  const { data: objectsData } = useListObjects();
  const objectOptions = useMemo(
    () =>
      (objectsData ?? [])
        .filter((object) => object.isActive)
        .map((object) => ({
          value: object.id ?? '',
          label: formatObjectLabel(object)
        }))
        .filter((option) => option.value),
    [objectsData, t]
  );

  const { data: connectionsData } = useListOrganisationConnections();
  const counterpartyOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );

  // Kontrakti se i dalje dohvaćaju u punom obimu — samo za razrješavanje
  // broja ugovora/naziva druge strane po retku, ne za pretragu (ide na backend preko /search).
  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];
  const contractMap = useMemo(() => {
    const map = new Map<string, Contract>();
    contracts.forEach((contract) => {
      if (contract.id) map.set(contract.id, contract);
    });
    return map;
  }, [contracts]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const newReportButton = (mobile?: boolean) =>
    mobile ? (
      <MobileFab onClick={() => setGenerateOpened(true)}>{t('weekReports.newReport')}</MobileFab>
    ) : (
      <Button onClick={() => setGenerateOpened(true)}>{t('weekReports.newReport')}</Button>
    );

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <Group justify="space-between" wrap="nowrap" align="flex-start" visibleFrom="sm">
          <HeaderIntro />
          <Group gap={10}>{newReportButton()}</Group>
        </Group>
        <Stack gap={12} hiddenFrom="sm">
          <HeaderIntro />
          {newReportButton(true)}
        </Stack>

        <GenerateWeekReportModal opened={generateOpened} onClose={() => setGenerateOpened(false)} />

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('weekReports.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              },
              {
                key: 'counterparty',
                label: t('weekReports.filterCounterparty'),
                options: counterpartyOptions,
                value: counterpartyFilter,
                onChange: setCounterpartyFilter
              },
              {
                key: 'object',
                label: t('weekReports.filterObject'),
                options: objectOptions,
                value: objectFilter,
                onChange: setObjectFilter
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={280}>
            <SearchField
              hideLabel
              placeholder={t('weekReports.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <Box w={220}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('weekReports.filterCounterparty')}
              data={counterpartyOptions}
              value={counterpartyFilter}
              onChange={setCounterpartyFilter}
              clearable
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('weekReports.filterObject')}
              data={objectOptions}
              value={objectFilter}
              onChange={setObjectFilter}
              clearable
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('weekReports.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                    <div
                      className="table-header-row"
                      style={{
                        ...tableRowStyle,
                        padding: '12px 20px',
                        backgroundColor: 'var(--color-bg-canvas)',
                        borderBottom: '1px solid var(--color-border-soft)'
                      }}
                    >
                      {DESKTOP_COLUMNS.map(([key, width, align]) => (
                        <Text
                          key={key}
                          fz={10}
                          fw={600}
                          w={width}
                          ta={align}
                          style={{
                            color: 'var(--color-text-muted)',
                            letterSpacing: 0.4,
                            whiteSpace: 'pre-line'
                          }}
                        >
                          {t(key)}
                        </Text>
                      ))}
                      <Text
                        fz={10}
                        fw={600}
                        w={TABLE_ACTION_COL_WIDTH}
                        style={{
                          ...tableCenteredCellStyle,
                          color: 'var(--color-text-muted)',
                          letterSpacing: 0.4
                        }}
                      >
                        {t('weekReports.colAction')}
                      </Text>
                    </div>

                    {pageItems.map((report, index) => {
                      const contract = report.contractId
                        ? contractMap.get(report.contractId)
                        : undefined;
                      const config = statusConfig[report.status ?? WeekReportStatus.DRAFT];

                      return (
                        <div
                          key={report.id}
                          style={{
                            ...tableRowStyle,
                            padding: '14px 20px',
                            borderTop:
                              index === 0 ? undefined : '1px solid var(--color-border-soft)'
                          }}
                        >
                          <Text
                            fz={13.5}
                            fw={600}
                            w={TABLE_WEEK_PRIMARY_COL_WIDTH}
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {formatWeekLabel(report.weekNumber, report.reportYear)}
                          </Text>
                          <Stack
                            gap={1}
                            w={260}
                            style={{ cursor: report.contractId ? 'pointer' : undefined }}
                            role={report.contractId ? 'button' : undefined}
                            tabIndex={report.contractId ? 0 : undefined}
                            onClick={() =>
                              report.contractId && navigate(`/contracts/${report.contractId}`)
                            }
                            onKeyDown={(event) => {
                              if ((event.key === 'Enter' || event.key === ' ') && report.contractId)
                                navigate(`/contracts/${report.contractId}`);
                            }}
                          >
                            <Text
                              fz={13.5}
                              fw={600}
                              truncate
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              {contract?.contractNumber ?? '—'}
                            </Text>
                            <Text fz={11.5} truncate style={{ color: 'var(--color-text-muted)' }}>
                              {[
                                report.objectName,
                                contract ? otherPartyName(contract, user?.orgName) : undefined
                              ]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </Text>
                          </Stack>
                          <Text fz={13} w={120} ta="right" style={cellStyle}>
                            {formatAmount(report.totalAmount)}
                          </Text>
                          <Text
                            fz={12}
                            w={TABLE_DATE_COL_WIDTH}
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            {formatDate(report.updatedAt)}
                          </Text>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                            <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                          </div>
                          <Group w={TABLE_ACTION_COL_WIDTH} justify="center">
                            <RowActionsMenu
                              detailsHref={`/weekly-reports/${report.id}`}
                              historyHref={`/weekly-reports/${report.id}/history`}
                            />
                          </Group>
                        </div>
                      );
                    })}
                    {pageItems.length > 0 && (
                      <ListTotalRow
                        columnWidths={DESKTOP_COLUMNS.map(([, width]) => width)}
                        amounts={{ 2: totalAmount }}
                        actionColumnWidth={TABLE_ACTION_COL_WIDTH}
                      />
                    )}
                  </div>
                </div>

                <ListPagination
                  label={t('weekReports.showing', {
                    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                    to: Math.min(page * pageSize, totalElements),
                    total: totalElements
                  })}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('weekReports.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((report) => {
                const contract = report.contractId ? contractMap.get(report.contractId) : undefined;
                const config = statusConfig[report.status ?? WeekReportStatus.DRAFT];

                const isDraft = report.status === WeekReportStatus.DRAFT;

                return (
                  <Stack
                    key={report.id}
                    gap={14}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-default)',
                      backgroundColor: 'var(--color-bg-surface)'
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                        {formatWeekLabel(report.weekNumber, report.reportYear)}
                      </Text>
                      <RowActionsMenu
                        detailsHref={`/weekly-reports/${report.id}`}
                        historyHref={`/weekly-reports/${report.id}/history`}
                      />
                    </Group>
                    <Stack gap={6}>
                      <Group justify="space-between">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('weekReports.colContract')}
                        </Text>
                        <Text
                          fz={12.5}
                          style={{
                            ...cellStyle,
                            cursor: report.contractId ? 'pointer' : undefined
                          }}
                          role={report.contractId ? 'button' : undefined}
                          tabIndex={report.contractId ? 0 : undefined}
                          onClick={() =>
                            report.contractId && navigate(`/contracts/${report.contractId}`)
                          }
                          onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && report.contractId)
                              navigate(`/contracts/${report.contractId}`);
                          }}
                        >
                          {contract?.contractNumber ?? '—'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('weekReports.colAmount')}
                        </Text>
                        <Text fz={12.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                          {formatAmount(report.totalAmount)}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('weekReports.colObject')}
                        </Text>
                        <Text
                          fz={12.5}
                          style={{ ...cellStyle, cursor: report.objectId ? 'pointer' : undefined }}
                          role={report.objectId ? 'button' : undefined}
                          tabIndex={report.objectId ? 0 : undefined}
                          onClick={() =>
                            report.objectId &&
                            navigate(`/templates/object-registry/${report.objectId}`)
                          }
                          onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && report.objectId)
                              navigate(`/templates/object-registry/${report.objectId}`);
                          }}
                        >
                          {report.objectName ?? '—'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {t('weekReports.colSentDate')}
                        </Text>
                        <Text fz={12.5} style={cellStyle}>
                          {isDraft ? '—' : formatDate(report.updatedAt)}
                        </Text>
                      </Group>
                    </Stack>
                    <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                  </Stack>
                );
              })}

              <ListTotalCard amount={totalAmount} />

              <ListPagination
                label={t('weekReports.showing', {
                  from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, totalElements),
                  total: totalElements
                })}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
